import React, { useEffect, useCallback, useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import classNames from "classnames";
import { Button } from "Components/ReactstrapSlim";
import { LayoutContext } from "Components/LayoutContext";
import * as Icons from "Components/Icons";
import { Module, ModuleCaption } from "Components/Module";
import { CollectionsClient, CollectionType, FavoriteCollectionViewModel, FavoriteWorkspaceViewModel, ICollaboratorViewModel } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { CollectionMenu } from "./CollectionMenu";
import { Workspace } from "./Workspace";

export interface WorkspacesProps {
   id: Guid
}

export const Workspaces: React.FC<WorkspacesProps> = ({ id }) => {
   const [collaborators, setCollaborators] = useState<ICollaboratorViewModel[] | undefined>();
   const collectionClient = useApiClient(CollectionsClient);
   const layoutContext = useContext(LayoutContext);   
   const history = useHistory();
   const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState<boolean>(true);
   const [workspaces, setWorkspaces] = useState<FavoriteWorkspaceViewModel[]>([]);
   const [collection, setCollection] = useState<FavoriteCollectionViewModel>();
   const [name, setName] = useState<string | undefined>();
   const [collectionType, setCollectionType] = useState<CollectionType | undefined>();
   const [numberOfSpaces, setNumberOfSpaces] = useState<number>(0);
   const [collectionsCount, setCollectionsCount] = useState<number>(0);
   const [collectionName, setCollectionsName] = useState<string | undefined>("");
   const [isError, setIsError] = useState<boolean>(false);

   const loadCollectionsData = async (abortSignal: AbortSignal, workspaceId?: Guid) => {
      setIsLoadingWorkspaces(true);
      try {
         const collaboratorsData = await collectionClient.collectionCollaborators(id, abortSignal);
         const collectionsCountData = await collectionClient.count(abortSignal);

         setCollaborators(collaboratorsData || []);
         setCollectionsCount(collectionsCountData);

         collectionClient.workspaces(id, abortSignal).then(result => {
            const workspacesData = result;
            if (workspacesData && workspacesData.workspaces) {
               setNumberOfSpaces(workspacesData.workspaces?.length);
            }

            setName(workspacesData.collection?.name);
            setCollectionType(workspacesData.collection?.collectionType);
            setWorkspaces(workspacesData.workspaces?.filter(w => w.id !== workspaceId) || []);
            setCollectionsName(workspacesData.collection?.name);
            setCollection(workspacesData.collection);
         }).catch(error => {
            setIsError(true);
         }).finally(() => {
            setIsLoadingWorkspaces(false);
         });
      }
      catch { }
   }

   const removeWorkspaceData = useCallback((collectionId: string, workspaceId: string, workspaceCount: number) => {
      layoutContext.showMessage(`Workspace ${collectionName} was successfully removed`);

      const abortController = new AbortController();
      loadCollectionsData(abortController.signal, workspaceId);

      if (workspaceCount === 0) {
         history.push("/collections");
      }

   }, []);

   const deleteCallback = useCallback(async () => {
      history.push("/collections");
   }, []);

   const renameCallback = useCallback(async (newName: string | undefined) => {
      setCollectionsName(newName);
   }, []);

   useEffect(() => {
      const abortController = new AbortController();
      loadCollectionsData(abortController.signal);

      return function cleanup() {
         abortController.abort();
      }
   }, []);

   return (<>
      <Link className="back-collections-link" to={"/collections"}><Icons.ArrowLeftAlt /><span className="back-collections-link-text">Back to Collections</span></Link>
      <Module size="lg" className={classNames("module-no-workspaces", { "d-none": isLoadingWorkspaces || workspaces.length > 0 })}>
         <ModuleCaption className={classNames("title", { "mb-px24": isError })}>{isError ? "Looks like this collection isn't available anymore" : "You don't have any favorite workspaces in this collection yet"}</ModuleCaption>
         {!isError && <div className="subtitle">Explore spaces nearby and add locations to your collection</div>}
         <div className="btn-wrapper">
            <Button color="arctic-20" size="sm" onClick={() => history.push("/collections")}>Go back</Button>
         </div>
      </Module>
      <Module size="lg" loading={isLoadingWorkspaces} className={classNames("module-workspaces", { "d-none": !isLoadingWorkspaces && workspaces.length == 0 })}>
         <ModuleCaption>
            <div className="collection-name">{collectionName}</div>
            <div className="right">
               <CollectionMenu collection={{ id, name, collaborators, collectionType, numberOfSpaces }} collectionUrl={collection?.url} renameCallback={renameCallback} deleteCallback={deleteCallback} count={collectionsCount} />
            </div>
         </ModuleCaption>
         <div className="workspaces-wrapper">
            {workspaces.map(w => <Workspace key={w.id} workspace={w} collectionType={collectionType || CollectionType.Regular} collectionId={id} removeWorkspaceCallback={removeWorkspaceData} />)}
         </div>
      </Module>
   </>);
}